import * as React from 'react';

import { cn, getColorHash, downloadFromUrl } from '@rematter/ui-web';
import { Button } from '@rematter/ui-web';
import bytes from 'bytes';
import { LucideTrash2 } from 'lucide-react';

import { useGenFileDownloadUrl } from '../../_graphql/mutationFileDownloadUrl';
import { FileRecord } from '../../types';

type FileCardProps = {
  file: FileRecord;
  onFileDelete?: (file: { id: string }) => Promise<boolean>;
};

export const FileCard = ({ file, onFileDelete }: FileCardProps) => {
  const extension = file.fileName?.match(/\.([^.]+)$/)?.[1] ?? '';
  const { mutateAsync: genFileDownloadUrl } = useGenFileDownloadUrl();

  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleFileClick = async () => {
    const [{ url }] = await genFileDownloadUrl({ fileIds: [file.id] });

    if (url) {
      downloadFromUrl(url, file.fileName ?? '');
    }
  };

  return (
    <div
      key={file.id}
      className={cn(
        '@FileCard flex cursor-pointer items-center gap-2 rounded-md border border-neutral/20 p-2 hover:bg-neutral/5',
        isDeleting && 'opacity-50 hover:bg-transparent',
      )}
      onClick={handleFileClick}
    >
      <div
        className="flex size-[50px] items-center justify-center rounded p-3 text-xs font-semibold text-white uppercase"
        style={{
          backgroundColor: getColorHash(extension, {
            saturation: 70,
            lightness: 50,
            alpha: 0.2,
          }),
          color: getColorHash(extension, {
            saturation: 70,
            lightness: 30,
          }),
        }}
      >
        {file.fileName?.match(/\.([^.]+)$/)?.[1]}
      </div>
      <div className="flex w-full flex-col items-stretch">
        <div className="flex w-full">
          <div className="w-0 flex-grow truncate text-sm">{file.fileName}</div>
        </div>
        <div className="text-xs text-neutral/60">
          {bytes(file.fileSize ?? 0, { decimalPlaces: 0 })}
        </div>
      </div>
      {onFileDelete && (
        <div className="flex items-center justify-center">
          <Button
            variant="text"
            color="failure"
            onClick={(e) => {
              e.stopPropagation();
              setIsDeleting(true);
              onFileDelete?.(file).finally(() =>
                setTimeout(() => setIsDeleting(false), 500),
              );
            }}
            isDisabled={isDeleting}
          >
            <LucideTrash2 size={16} />
          </Button>
        </div>
      )}
    </div>
  );
};
